import { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  ChevronDown, ChevronRight, Download, Loader2, Link2, Unlink, Package, FileText, Users, AlertTriangle, Printer, X, Search, Filter
} from 'lucide-react';
import { PackingPrintPreview } from './PackingPrintPreview';
import { StickerLine } from '../components/Stickers';
import { normalizeCategory } from '../lib/categories';

const PRODUCTION_DAYS = ['FRI', 'SAT', 'SUN', 'MON', 'TUES', 'WED', 'THURS'];

const formatWeight = (g, weightType) => {
  if (weightType === 'pieces' || weightType === 'count') {
    const val = g % 1 === 0 ? g : g.toFixed(1);
    return `${val} pcs`;
  }
  if (g >= 1000) return `${(g / 1000).toFixed(2)}kg`;
  return `${Math.round(g)}g`;
};

const PackingTab = ({ selectedOrderId }) => {
  const [selectedDays, setSelectedDays] = useState([]);
  const [packingData, setPackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expandedRecipes, setExpandedRecipes] = useState(new Set());
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const printRef = useRef(null);
  const [linkedComponents, setLinkedComponents] = useState({});
  const [linkableComponents, setLinkableComponents] = useState({});
  // Client selection
  const [allClients, setAllClients] = useState([]); // {name, key} from order data
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [appliedClients, setAppliedClients] = useState([]); // filter behind the data on screen ([] = all)
  const [clientFilterOpen, setClientFilterOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  const toggleDay = (day) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
    // Reset clients when days change
    setAllClients([]);
    setSelectedClients(new Set());
    setPackingData(null);
  };

  const toggleClient = (name) => {
    setSelectedClients(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const selectAllClients = () => setSelectedClients(new Set(allClients.map(c => c.name)));
  const deselectAllClients = () => setSelectedClients(new Set());

  const filteredClientList = useMemo(() => allClients.filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    (c.key || '').toLowerCase().includes(clientSearch.toLowerCase())
  ), [allClients, clientSearch]);

  const applyClientFilter = () => {
    fetchPackingData();
  };

  const fetchPackingData = async () => {
    if (!selectedOrderId || selectedDays.length === 0) {
      toast.error('Select at least one production day');
      return;
    }
    setLoading(true);
    try {
      const params = { days: selectedDays.join(',') };
      // If clients are filtered (not all selected), pass the filter
      const filter = selectedClients.size > 0 && selectedClients.size < allClients.length ? [...selectedClients] : [];
      if (filter.length) params.clients_json = JSON.stringify(filter);
      const r = await axios.get(`${API}/packing-sheets/${selectedOrderId}`, { params });
      setPackingData(r.data);
      setAppliedClients(filter);
      setLinkedComponents({});
      detectLinkableComponents(r.data.recipe_sheets);
      
      // Extract unique clients from the full (unfiltered) data for the filter panel
      if (allClients.length === 0) {
        // Fetch full data once to get all clients
        const fullR = await axios.get(`${API}/packing-sheets/${selectedOrderId}`, {
          params: { days: selectedDays.join(',') }
        });
        const clientMap = {};
        for (const rs of fullR.data.recipe_sheets || []) {
          for (const c of [...(rs.individual_clients || []), ...(rs.sharing_clients || [])]) {
            if (c.client_name && !clientMap[c.client_name]) {
              clientMap[c.client_name] = { name: c.client_name, key: c.key };
            }
          }
        }
        const clientList = Object.values(clientMap).sort((a, b) => a.name.localeCompare(b.name));
        setAllClients(clientList);
        setSelectedClients(new Set(clientList.map(c => c.name)));
      }
    } catch (e) {
      toast.error('Failed to load packing data');
    } finally {
      setLoading(false);
    }
  };

  const detectLinkableComponents = (sheets) => {
    // Map component_name -> [{ recipe_id, recipe_name, clients with totals }]
    const compMap = {};
    for (const sheet of sheets) {
      for (const sc of sheet.sharing_clients || []) {
        for (const ct of sc.component_totals || []) {
          const key = ct.component_name;
          if (!compMap[key]) compMap[key] = [];
          compMap[key].push({
            recipe_id: sheet.recipe_id,
            recipe_name: sheet.recipe_name,
            client_name: sc.client_name,
            client_key: sc.key,
            total: ct.total_quantity,
          });
        }
      }
    }
    // Only keep components that appear in 2+ different recipes
    const linkable = {};
    for (const [name, entries] of Object.entries(compMap)) {
      const uniqueRecipes = new Set(entries.map(e => e.recipe_id));
      if (uniqueRecipes.size >= 2) {
        linkable[name] = entries;
      }
    }
    setLinkableComponents(linkable);
  };

  const toggleLinkComponent = (componentName) => {
    setLinkedComponents(prev => {
      const updated = { ...prev };
      if (updated[componentName]) {
        delete updated[componentName];
      } else {
        const entries = linkableComponents[componentName] || [];
        const recipeIds = [...new Set(entries.map(e => e.recipe_id))];
        // First recipe in the sorted order gets the consolidated entry
        const sheets = packingData?.recipe_sheets || [];
        const orderedIds = sheets.map(s => s.recipe_id);
        const sortedRids = recipeIds.sort((a, b) => orderedIds.indexOf(a) - orderedIds.indexOf(b));
        updated[componentName] = {
          first_recipe_id: sortedRids[0],
          recipe_ids: sortedRids,
        };
      }
      return updated;
    });
  };

  const toggleExpand = (id) => {
    setExpandedRecipes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exportPDF = async () => {
    if (!selectedOrderId || selectedDays.length === 0) return;
    setExporting(true);
    try {
      // Build linked_components payload
      const linkedPayload = {};
      for (const [name, info] of Object.entries(linkedComponents)) {
        linkedPayload[name] = {
          first_recipe_id: info.first_recipe_id,
          recipe_ids: info.recipe_ids,
        };
      }

      const response = await axios.post(
        `${API}/packing-sheets/${selectedOrderId}/export-pdf`,
        {
          days: selectedDays,
          linked_components: linkedPayload,
          clients: appliedClients,
        },
        { responseType: 'blob' }
      );

      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Packing_Sheets_${selectedDays.join('_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handlePrintPreview = () => {
    setShowPrintPreview(true);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          const doc = printWindow.document;
          doc.open();
          const html = doc.createElement('html');
          const head = doc.createElement('head');
          const title = doc.createElement('title');
          title.textContent = `Packing Sheets - ${selectedDays.join(' + ')}`;
          head.appendChild(title);
          const style = doc.createElement('style');
          style.textContent = `
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Helvetica, Arial, sans-serif; font-size: 9pt; color: #1a1a2e; }
            .page { page-break-after: always; padding: 10mm 15mm; }
            .page:last-child { page-break-after: auto; }
            .header { display: flex; justify-content: space-between; align-items: center; background: #f0f0f0; padding: 6px 10px; border: 1px solid #ccc; margin-bottom: 8px; }
            .header span { font-size: 9pt; }
            .header .title { font-weight: bold; font-size: 10pt; }
            h2 { font-size: 13pt; margin: 6px 0; text-transform: uppercase; }
            h3 { font-size: 9pt; margin: 8px 0 4px; text-transform: uppercase; color: #555; letter-spacing: 0.5px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
            th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; font-size: 8pt; }
            th { background: #e8e8e8; font-weight: bold; }
            .text-center { text-align: center; }
            .font-bold { font-weight: bold; }
            .allergy { background: #fff0f0; border: 1px solid #fcc; padding: 6px; margin-top: 6px; color: #c00; font-size: 8pt; }
            .linked-badge { background: #d1fae5; color: #065f46; font-size: 7pt; padding: 1px 4px; border-radius: 3px; font-weight: bold; }
            .skipped { opacity: 0.3; text-decoration: line-through; }
            @media print { .page { padding: 5mm 10mm; } }
          `;
          head.appendChild(style);
          html.appendChild(head);
          const body = doc.createElement('body');
          body.innerHTML = printRef.current.innerHTML;
          html.appendChild(body);
          doc.appendChild(html);
          doc.close();
          printWindow.focus();
          setTimeout(() => printWindow.print(), 300);
        }
      }
      setShowPrintPreview(false);
    }, 100);
  };

  // Helper: does a given client (by name OR key) have a sharing entry in `recipeId`?
  const clientHasSharingEntryInRecipe = (recipeId, clientName, clientKey) => {
    const sheets = packingData?.recipe_sheets || [];
    const sheet = sheets.find(s => s.recipe_id === recipeId);
    if (!sheet) return false;
    return (sheet.sharing_clients || []).some(sc =>
      sc.client_name === clientName || (clientKey && sc.key === clientKey)
    );
  };

  // Check if a sharing component row should be shown as "linked away" (skipped) for a given recipe + client
  // Only hide if this same client ALSO has a matching sharing entry on the first/linked recipe,
  // otherwise the quantity would silently disappear from packing.
  const isLinkedAway = (recipeId, componentName, clientName, clientKey) => {
    const link = linkedComponents[componentName];
    if (!link) return false;
    if (!link.recipe_ids.includes(recipeId)) return false;
    if (link.first_recipe_id === recipeId) return false;
    // Per-client safety check: only hide if the client exists on the first recipe too
    return clientHasSharingEntryInRecipe(link.first_recipe_id, clientName, clientKey);
  };

  // Get extra total added to first recipe from linking — PER CLIENT only
  const getLinkedExtra = (recipeId, componentName, clientName, clientKey) => {
    const link = linkedComponents[componentName];
    if (!link || link.first_recipe_id !== recipeId) return 0;
    let extra = 0;
    const sheets = packingData?.recipe_sheets || [];
    for (const sheet of sheets) {
      if (link.recipe_ids.includes(sheet.recipe_id) && sheet.recipe_id !== recipeId) {
        for (const sc of sheet.sharing_clients || []) {
          // Only add quantities for the SAME client (match by name OR key)
          if (sc.client_name !== clientName && sc.key !== clientKey) continue;
          for (const ct of sc.component_totals || []) {
            if (ct.component_name === componentName) {
              extra += ct.total_quantity;
            }
          }
        }
      }
    }
    return extra;
  };

  // For UI affordance: which client-recipe pairs got consolidated by a link?
  const getLinkConsolidationMap = (componentName) => {
    const link = linkedComponents[componentName];
    if (!link) return { consolidated: [], orphans: [] };
    const sheets = packingData?.recipe_sheets || [];
    const firstSheet = sheets.find(s => s.recipe_id === link.first_recipe_id);
    if (!firstSheet) return { consolidated: [], orphans: [] };
    const firstClients = firstSheet.sharing_clients || [];

    const consolidated = []; // [{client_name, key, from_recipe_name}]
    const orphans = [];      // [{client_name, key, from_recipe_name}] -> stay visible on own sheet
    for (const sheet of sheets) {
      if (!link.recipe_ids.includes(sheet.recipe_id)) continue;
      if (sheet.recipe_id === link.first_recipe_id) continue;
      for (const sc of sheet.sharing_clients || []) {
        const hasComp = (sc.component_totals || []).some(ct => ct.component_name === componentName);
        if (!hasComp) continue;
        const matchInFirst = firstClients.some(fc => fc.client_name === sc.client_name || (sc.key && fc.key === sc.key));
        const entry = { client_name: sc.client_name, key: sc.key, from_recipe_name: sheet.recipe_name };
        if (matchInFirst) consolidated.push(entry);
        else orphans.push(entry);
      }
    }
    return { consolidated, orphans };
  };

  const hasLinkableItems = Object.keys(linkableComponents).length > 0;

  return (
    <div data-testid="packing-tab">
      {/* Day Selection */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Select Production Days</h3>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {PRODUCTION_DAYS.map(day => (
            <button
              key={day}
              onClick={() => toggleDay(day)}
              className={`px-4 py-2 text-sm font-medium border transition-all ${
                selectedDays.includes(day)
                  ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
              style={{ borderRadius: '6px' }}
              data-testid={`packing-day-${day}`}
            >
              {day}
            </button>
          ))}
        </div>
        <Button
          onClick={fetchPackingData}
          disabled={selectedDays.length === 0 || loading}
          className="btn-primary"
          data-testid="load-packing-data-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
          Load Packing Data
        </Button>
      </div>

      {/* Client Selection Filter */}
      {allClients.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg mb-5" data-testid="client-filter-panel">
          <button
            onClick={() => setClientFilterOpen(!clientFilterOpen)}
            data-testid="client-filter-toggle"
            className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-semibold text-gray-700">Filter Clients</span>
              <span className="text-xs text-gray-400">
                ({selectedClients.size}/{allClients.length} selected)
              </span>
            </div>
            {clientFilterOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          </button>
          {clientFilterOpen && (
            <div className="px-5 pb-4 border-t border-gray-100">
              <div className="flex items-center gap-2 mt-3 mb-3">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
                  <Input
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Search clients..."
                    className="pl-8 h-9 text-sm"
                    data-testid="client-search-input"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={selectAllClients} className="text-xs" data-testid="select-all-clients-btn">Select All</Button>
                <Button variant="outline" size="sm" onClick={deselectAllClients} className="text-xs" data-testid="deselect-all-clients-btn">Deselect All</Button>
                <Button size="sm" onClick={applyClientFilter} disabled={selectedClients.size === 0} className="btn-primary text-xs" data-testid="apply-client-filter-btn">
                  Apply
                </Button>
              </div>
              {selectedClients.size === 0 && <p className="text-xs text-amber-700 mb-2" data-testid="no-clients-selected-msg">Tick at least one client to apply a filter.</p>}
              {appliedClients.length > 0 && (
                <p className="text-xs text-gray-500 mb-2" data-testid="applied-filter-summary">Showing {appliedClients.length} of {allClients.length} clients. Click Apply after changing ticks.</p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                {filteredClientList.map(c => (
                  <label key={c.name} className={`flex items-center gap-2 p-2 rounded-lg border text-xs cursor-pointer transition-all ${
                    selectedClients.has(c.name) ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200 opacity-50'
                  }`}>
                    <Checkbox
                      checked={selectedClients.has(c.name)}
                      onCheckedChange={() => toggleClient(c.name)}
                      data-testid={`client-filter-checkbox-${c.name}`}
                      className="scale-75"
                    />
                    <span className="truncate font-medium">{c.name}</span>
                    {c.key && <span className="shrink-0 px-1.5 py-0.5 bg-gray-100 text-[9px] font-bold rounded">{c.key}</span>}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Packing Data Dashboard */}
      {packingData && (
        <div>
          {/* Summary */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-3">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Recipes</div>
                <div className="text-2xl font-bold tabular-nums">{packingData.recipe_sheets.length}</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Days</div>
                <div className="text-2xl font-bold tabular-nums">{packingData.selected_days.join(', ')}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handlePrintPreview}
                disabled={packingData.recipe_sheets.length === 0}
                variant="outline"
                className="text-sm"
                data-testid="print-preview-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Preview
              </Button>
              <Button
                onClick={exportPDF}
                disabled={exporting || packingData.recipe_sheets.length === 0}
                className="btn-primary text-sm"
                data-testid="export-packing-pdf-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Export PDF
              </Button>
            </div>
          </div>

          {/* Linkable Components Panel */}
          {hasLinkableItems && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5" data-testid="linkable-components-panel">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="w-4 h-4 text-amber-600" />
                <h3 className="text-sm font-semibold text-amber-800">Shared Components Detected</h3>
              </div>
              <p className="text-xs text-amber-700 mb-3">
                These components appear across multiple recipes for sharing clients. Link them to consolidate packing into one entry.
              </p>
              <div className="space-y-2">
                {Object.entries(linkableComponents).map(([compName, entries]) => {
                  const isLinked = !!linkedComponents[compName];
                  const recipeNames = [...new Set(entries.map(e => e.recipe_name))];
                  const totalQty = entries.reduce((sum, e) => sum + e.total, 0);
                  const { consolidated, orphans } = isLinked
                    ? getLinkConsolidationMap(compName)
                    : { consolidated: [], orphans: [] };
                  return (
                    <div
                      key={compName}
                      className={`p-3 rounded-lg border transition-all ${
                        isLinked ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{compName}</div>
                          <div className="text-xs text-gray-500 truncate">
                            {recipeNames.join(' + ')} | Total: {formatWeight(totalQty)}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleLinkComponent(compName)}
                          className={`text-xs ml-3 ${isLinked ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-100' : ''}`}
                          data-testid={`link-component-${compName.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {isLinked ? <><Unlink className="w-3 h-3 mr-1" /> Unlink</> : <><Link2 className="w-3 h-3 mr-1" /> Link</>}
                        </Button>
                      </div>
                      {isLinked && (
                        <div className="mt-2 pt-2 border-t border-emerald-200 text-[11px] space-y-1" data-testid={`link-detail-${compName.replace(/\s+/g, '-').toLowerCase()}`}>
                          {consolidated.length > 0 && (
                            <div className="text-emerald-800">
                              <span className="font-semibold">Consolidated onto first recipe ({consolidated.length}):</span>{' '}
                              {consolidated.map(c => `${c.client_name}${c.key ? ` (${c.key})` : ''}`).join(', ')}
                            </div>
                          )}
                          {orphans.length > 0 && (
                            <div className="text-amber-800 flex items-start gap-1">
                              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                              <span>
                                <span className="font-semibold">Kept separate (no order on first recipe — {orphans.length}):</span>{' '}
                                {orphans.map(o => `${o.client_name}${o.key ? ` (${o.key})` : ''} [${o.from_recipe_name}]`).join(', ')}
                              </span>
                            </div>
                          )}
                          {consolidated.length === 0 && orphans.length === 0 && (
                            <div className="text-gray-500 italic">No sharing client overlaps for this component.</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recipe Cards */}
          {packingData.recipe_sheets.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm">No recipes with packing sheets found for selected days</p>
            </div>
          ) : (
            <div className="space-y-3">
              {packingData.recipe_sheets.map(sheet => {
                const isExpanded = expandedRecipes.has(sheet.recipe_id);
                const totalClients = sheet.individual_clients.length + sheet.sharing_clients.length;
                const totalQty = sheet.individual_clients.reduce((s, c) => s + c.quantity, 0)
                  + sheet.sharing_clients.reduce((s, c) => s + c.quantity, 0);

                return (
                  <div key={sheet.recipe_id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`packing-recipe-${sheet.recipe_id}`}>
                    {/* Recipe Header */}
                    <div
                      className="flex items-center gap-3 px-5 py-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleExpand(sheet.recipe_id)}
                    >
                      {isExpanded
                        ? <ChevronDown className="w-5 h-5 text-gray-400" />
                        : <ChevronRight className="w-5 h-5 text-gray-400" />
                      }
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">{sheet.recipe_name}</div>
                        <StickerLine stickers={sheet.stickers} testId={`packing-stickers-${sheet.recipe_id}`} className="mt-1.5 inline-flex" />
                        <div className="flex items-center gap-2 mt-1">
                          <span className="px-2 py-0.5 bg-zinc-100 text-[10px] uppercase tracking-wider font-bold text-zinc-600">{normalizeCategory(sheet.category)}</span>
                          <span className="text-xs text-gray-500">
                            <Users className="w-3 h-3 inline mr-1" />{totalClients} clients
                          </span>
                        </div>
                      </div>
                      <span className="tabular-nums font-bold text-lg">{totalQty} ptn</span>
                    </div>

                    {/* Expanded Detail */}
                    {isExpanded && (
                      <div className="px-5 pb-5 border-t border-gray-100">
                        {/* Allergy Notes at Top */}
                        {(() => {
                          const allergies = [...sheet.individual_clients, ...sheet.sharing_clients].filter(c => c.allergy_notes);
                          if (allergies.length === 0) return null;
                          return (
                            <div className="mt-3 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <div className="flex items-center gap-2 mb-1">
                                <AlertTriangle className="w-4 h-4 text-red-600" />
                                <span className="text-xs font-bold text-red-700 uppercase tracking-wider">Allergy Notes</span>
                              </div>
                              {allergies.map((c) => (
                                <div key={c.client_name} className="text-sm text-red-700">
                                  <strong>{c.client_name} ({c.key})</strong>: {c.allergy_notes}
                                </div>
                              ))}
                            </div>
                          );
                        })()}

                        {/* Component Weights Table */}
                        <div className="mt-4 mb-4">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Component Weights</h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm border border-gray-200">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 border-b">Component</th>
                                  {sheet.size_names.map(sn => (
                                    <th key={sn} className="text-center py-2 px-3 text-xs font-semibold text-gray-600 border-b">{sn}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(sheet.component_weights).map(([compName, info]) => (
                                  <tr key={compName} className="border-b border-gray-100">
                                    <td className="py-2 px-3 font-medium text-sm">{compName}</td>
                                    {sheet.size_names.map(sn => (
                                      <td key={sn} className="py-2 px-3 text-center tabular-nums font-semibold">
                                        {info.sizes[sn] !== undefined ? info.sizes[sn] : '-'}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Individual Clients (Complete Meals) */}
                        {sheet.individual_clients.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Complete Meals</h4>
                            <table className="w-full text-sm border border-gray-200">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 border-b">Client</th>
                                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 border-b">Key</th>
                                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 border-b">Size</th>
                                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600 border-b">Qty</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sheet.individual_clients.map((c) => (
                                  <tr key={c.client_name} className={`border-b border-gray-100 ${c.allergy_notes ? 'bg-red-50' : ''}`}>
                                    <td className="py-2 px-3 font-medium">{c.client_name}</td>
                                    <td className="py-2 px-3 font-bold">{c.key}</td>
                                    <td className="py-2 px-3">{c.meal_size}</td>
                                    <td className="py-2 px-3 text-center tabular-nums font-bold">{c.quantity}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Sharing Clients */}
                        {sheet.sharing_clients.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Sharing Packs</h4>
                            <table className="w-full text-sm border border-gray-200">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 border-b">Component</th>
                                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 border-b">Client</th>
                                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 border-b">Key</th>
                                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 border-b">Size</th>
                                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-600 border-b">Total</th>
                                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 border-b">Packaging</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sheet.sharing_clients.map((sc, si) =>
                                  (sc.component_totals || []).map((ct, ci) => {
                                    const away = isLinkedAway(sheet.recipe_id, ct.component_name, sc.client_name, sc.key);
                                    const extra = getLinkedExtra(sheet.recipe_id, ct.component_name, sc.client_name, sc.key);
                                    const displayTotal = ct.total_quantity + extra;
                                    const isConsolidated = extra > 0;

                                    return (
                                      <tr
                                        key={`${si}-${ci}`}
                                        className={`border-b border-gray-100 ${away ? 'opacity-30 line-through' : ''} ${isConsolidated ? 'bg-emerald-50' : ''}`}
                                      >
                                        <td className="py-2 px-3 font-medium">
                                          {ct.component_name}
                                          {ct.is_group && <span className="ml-1 text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold">GROUP</span>}
                                          {isConsolidated && <span className="ml-1 text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">LINKED</span>}
                                          {away && <span className="ml-1 text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-bold">LINKED ELSEWHERE</span>}
                                          {ct.component_names_in_group && ct.component_names_in_group.length > 0 && (
                                            <div className="text-[10px] text-gray-400 mt-0.5">{ct.component_names_in_group.join(' + ')}</div>
                                          )}
                                        </td>
                                        <td className="py-2 px-3">{sc.client_name}</td>
                                        <td className="py-2 px-3 font-bold">{sc.key}</td>
                                        <td className="py-2 px-3">{sc.meal_size || ''}</td>
                                        <td className="py-2 px-3 text-center tabular-nums font-bold">
                                          {away ? '-' : formatWeight(displayTotal, ct.weight_type)}
                                        </td>
                                        <td className="py-2 px-3 text-xs">
                                          {ct.packaging}
                                          {ct.packaging_notes && <span className="text-gray-500"> ({ct.packaging_notes})</span>}
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!packingData && !loading && (
        <div className="text-center py-16 text-gray-400">
          <Package className="w-16 h-16 mx-auto mb-3 opacity-20" />
          <p className="text-sm">Select production days and click &quot;Load Packing Data&quot; to begin</p>
        </div>
      )}

      {/* Hidden print content - rendered off-screen for print preview */}
      {showPrintPreview && packingData && (
        <PackingPrintPreview
          ref={printRef}
          packingData={packingData}
          isLinkedAway={isLinkedAway}
          getLinkedExtra={getLinkedExtra}
        />
      )}
    </div>
  );
};

export default PackingTab;
